/**
 * useVariantCRun — owns the SSE subscription + section-status map
 * + dispatch into the Variant C reducer.
 *
 * SRP: this hook is the bridge between (a) our existing streaming
 * backend and (b) the Variant C reducer + spine state. It does NOT:
 *   - know the design's component shapes (LoomSpine renders these
 *     state shapes; the hook just produces them)
 *   - own lifecycle state directly (the reducer does)
 *   - call startRun (the shell does — runId is passed in)
 *
 * What it produces:
 *   - sections: ordered SpineSection[] with per-section status,
 *     subline, citation count
 *   - activeSectionId: which section is currently drafting
 *   - subscribe lifecycle: opens an EventSource on mount when runId
 *     is set, closes on unmount or runId change
 *
 * Reuses subscribeToRun (existing api/sse.ts). Zero new SSE code.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getRunSnapshot } from "../../api/client";
import type { SpineItemDTO } from "../../api/client";
import { subscribeToRun } from "../../api/sse";
import type { ProgressEvent } from "../../types";
import { handleAuditSseEvent } from "../reviewStore";
import {
  type InitProgress,
  normalizeSpineLifecycle,
  type RunIdentity,
  type SpineSectionStatus,
  type SpineSectionUpdate,
  translateProgressEvent,
} from "../sseAdapter";
import type { VariantCEvent } from "../state";
import { orderSpineSections } from "../utils/spineOrder";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * One section as the spine sees it. Built from the doc-type config
 * (initial queued list) and updated incrementally by SSE events.
 */
export interface SpineSection {
  id: string;
  number: string; // display gutter (e.g. "9.1" or moduleId)
  title: string; // human-readable label
  status: SpineSectionStatus;
  /** Friendly description of what the agent is doing right now
   *  (e.g. "reading Protocol §4.2", "drafting…"). LoomSpine renders
   *  this under the title for the active section, with a live
   *  elapsed-time counter ("· 2.1s") computed from sublineStartedAt. */
  subline?: string;
  /** Wall-clock timestamp when ``subline`` was last set with a NEW
   *  signal. Drives the elapsed-time counter. Updates that change
   *  the subline text but not the timestamp (e.g. text_chunk during
   *  drafting) preserve the prior value. */
  sublineStartedAt?: number;
  citationCount?: number;
  /** Live retrieval progress while the agent is resolving requirements.
   *  Null once drafting begins — spine collapses any progress indicator. */
  retrievalProgress?: {
    resolved: number;
    total: number;
    writtenOff: number;
  } | null;
  /** Total info-requirement items identified at planning time. */
  requirementCount?: number;
  /** Spec 23 P3 — word count of the drafted text. Set on
   *  ``section_ready`` from ``section_response.draft_text`` (same
   *  derivation the EventLog adapter already uses). Undefined until
   *  the section is drafted. Renders in the drafted-subline. */
  wordCount?: number;
  /** Spec 23 P3 — quality verdict from the review-agent sidecar.
   *  Merged in at shell level from ``useRunVerdictWatcher``; the
   *  SSE adapter does NOT populate it (different data path). */
  quality?: "high" | "medium" | "low";
  /** Spec 23 P3 — count of unresolved findings in the sidecar
   *  verdict for this section. Drives the ``N⚐`` flag badge on
   *  the spine row. Same origin as ``quality``. */
  flaggedCount?: number;
  /** Count of draft content gap flags detected from ``flags.yaml``
   *  (category=gap). Drives the red ``N⚑`` spine badge. */
  contentGapCount?: number;
  /** Count of [REGULATOR FLAG: …] recommendations in the section, set by
   *  the regulatory audit. Drives the blue regulator-tag spine badge. */
  regulatorFlagCount?: number;
  /** Full per-flag-type counts keyed by ``flags.yaml`` id. */
  contentFlagCounts?: Record<string, number>;
  /** Spec 23 P3 — epoch ms when the section transitioned to
   *  ``drafted`` (captured in the SSE adapter at section_ready
   *  time). Drives the drafted-subline's "accepted Xpm" timestamp. */
  acceptedAt?: number;
  /** Persisted per-section token telemetry (input/output tokens and the
   *  drafting model backend). Populated on hydrate from
   *  ``GET /runs/{id}/all-sections``; undefined/0 for older runs or before a
   *  section has drafted. Rendered as a compact line on the spine row. */
  inputTokens?: number;
  outputTokens?: number;
  model?: string | null;
  /** Custom-submission template match. When this run is a custom submission
   *  (built on an existing Word doc), a section's heading is matched lazily to
   *  the doctype template on the first regenerate / regulatory audit. The
   *  result is persisted here so the Haiku match never re-runs.
   *    - ``matchResolved`` true once the check ran (matched OR no-match).
   *    - ``matchedModuleId`` / ``matchedTitle`` set only when a match was found. */
  matchedModuleId?: string | null;
  matchedTitle?: string | null;
  matchResolved?: boolean;
  /** Custom-submission per-section drafting instruction. The writer authors
   *  this in the Inspector's Instructions tab (custom runs have no template
   *  instruction to map onto); it's persisted on the ``custom_spine`` row and
   *  gates whether the section can be drafted. Undefined/empty until saved. */
  instruction?: string | null;
}

export interface UseVariantCRunParams {
  /** The active run's id, or null when no run is in flight. */
  runId: string | null;
  /** Initial section list — typically derived from the doc-type
   *  config so the spine renders queued nodes immediately, before
   *  any events arrive. */
  initialSections: SpineSection[];
  /** Reducer dispatch function from the shell. */
  dispatch: (event: VariantCEvent) => void;
  /** Optional side-effect callback fired BEFORE our own translation
   *  for every SSE event. The shell uses this to wire Word-side
   *  effects (scaffolding on gate_start, insertSectionContent on
   *  section_ready) without us coupling to Word.js here.
   *
   *  Single subscription, two consumers — keeps SRP intact. */
  onProgressEvent?: (event: ProgressEvent) => void;
  /** Bump this when the same runId needs a fresh SSE subscription. */
  subscriptionNonce?: number;
  /** Refresh-resume already hydrated from backend REST state, so it should
   *  attach to the live tail instead of replaying old stream history. */
  subscriptionStartMode?: "history" | "latest";
  /** Current reducer RunState — used to suppress the idle watchdog when the
   *  run is intentionally not streaming (paused / post-run / idle). */
  runState?: string;
  /** True when the run is in a terminal status (completed / failed /
   *  cancelled) per a prior REST fetch. Skip SSE entirely — the backend's
   *  Redis stream for that run is likely already evicted (server eviction
   *  loop runs every 30 min and sweeps runs older than 15 min), so an
   *  EventSource attempt would 404 and surface a misleading "Connection to
   *  server lost" banner. */
  terminalRunStatus?: boolean;
  /** True while a manual section audit is in flight for this run. A "Run review"
   *  can run on an idle/completed (terminal) run, which otherwise has no live
   *  SSE stream — so its live step/counter narration would never arrive. When
   *  set, the ``terminalRunStatus`` SSE gate is lifted so the audit's stream is
   *  opened (the server primes it via ``ensure_stream`` so it exists), the
   *  connection-lost banner is suppressed (this stream is ephemeral), and a
   *  brief 404 at connect (the prime/subscribe race) is retried rather than
   *  surfaced. */
  auditActive?: boolean;
  /** True while shell is restoring the run from a backend snapshot.
   *  During this window SSE must stay detached to avoid competing state
   *  mutations against the atomic restore projection. */
  restoreInFlight?: boolean;
  /** Identity recovered from the persisted session record on reopen.
   *  Seeds ``identity`` once when it arrives (asynchronously, after the
   *  hook has typically already mounted). A live ``run_metadata`` SSE
   *  event wins if it races ahead — the seed uses a functional update
   *  that only fills ``identity`` when still null. */
  initialIdentity?: RunIdentity | null;
}

export interface UseVariantCRunState {
  sections: SpineSection[];
  activeSectionId: string | null;
  /** Captured from the run_metadata SSE event. Used by the Inspector
   *  to construct the review_agent sidecar's sessionId. Null until
   *  the metadata event arrives (typically <500ms post-start). */
  identity: RunIdentity | null;
  /** Last error event surfaced by the SSE stream — null when healthy. */
  streamError: string | null;
  /** Init-phase progress emitted by the backend while the runner is
   *  being built (library loading, document indexing). Structured
   *  stage + counts drive the spine's init checklist panel. Null once
   *  run_metadata arrives and the run enters the module-generation phase. */
  initStatus: InitProgress | null;
  /** Imperative setter for one section's status — used by the
   *  approval popout to flip the row to ``needs_approval`` when the
   *  verdict watcher decides the spine should pause, and back to
   *  ``drafted`` when the writer marks the popout resolved. Status
   *  lives in spine state (not the reducer's lifecycle state)
   *  because it changes per-section, not per-run. */
  setSectionStatus: (sectionId: string, status: SpineSectionStatus) => void;
  /** Imperative setter for a custom section's drafting instruction — used by
   *  the Inspector's Instructions editor to reflect a save/clear onto the spine
   *  row (which gates the section's Generate / Draft-more buttons). ``null``
   *  clears it. */
  setSectionInstruction: (sectionId: string, instruction: string | null) => void;
  /** Seed the resume position (Redis stream entry id) for the next
   *  (re)subscribe — used by the bulk resolve flow so it replays the
   *  orchestrator's events from a captured cursor instead of tailing
   *  "latest" and race-missing the first module_start. */
  seedStreamCursor: (id: string | null) => void;
  /** Clear a section's activity subline/spinner (and optionally set a
   *  status) — used by proposal decision handlers so a missed
   *  section_state_changed event can't leave a spinner behind. */
  clearSectionActivity: (sectionId: string, status?: SpineSectionStatus) => void;
  /** Replace the full spine snapshot in one write. Used by document
   *  restore when the backend only has the UI snapshot, not section
   *  payloads. */
  hydrateSections: (nextSections: SpineSection[]) => void;
}

// ---------------------------------------------------------------------------
// Pure helpers — exported for direct testing
// ---------------------------------------------------------------------------

/**
 * Spine-row statuses that mean a section holds real, writer-visible draft
 * content that must survive a spine rebuild. Single source of truth for the
 * "Draft more sections" picker lock AND the two bulk-reseed effects in the
 * hook, so the set that locks a row in the picker is exactly the set carried
 * forward when the spine is rebuilt.
 */
export const SPINE_COMPLETED_STATUSES: ReadonlySet<SpineSectionStatus> = new Set([
  "drafted",
  "needs_approval",
  "flagged",
]);

/**
 * Phase 9.4 — compute the set of section ids that have ALREADY been
 * drafted (any status indicating real draft content exists). Used by
 * the "Draft more sections" picker to lock those rows so a follow-up
 * run cannot accidentally redraft (and overwrite) existing content.
 *
 * Locked statuses = ``drafted | needs_approval | flagged``. All three
 * mean the section's draft content has been written to Word at least
 * once and is now under writer review:
 *   - ``drafted``        → completed, awaiting accept
 *   - ``needs_approval`` → completed, verdict popout pending
 *   - ``flagged``        → completed, reviewer found issues
 *
 * Unlocked statuses = ``queued | drafting``. ``queued`` has no
 * content yet; ``drafting`` is a transient mid-run state that, if
 * observed post-run, indicates the section never finished — the
 * writer should be able to retry it.
 *
 * Pure; no React, no I/O. Returns a frozen array (caller cannot
 * mutate it back into the source).
 */
export function computeLockedModuleIds(sections: readonly SpineSection[]): ReadonlyArray<string> {
  const out: string[] = [];
  for (const s of sections) {
    if (SPINE_COMPLETED_STATUSES.has(s.status)) {
      out.push(s.id);
    }
  }
  return Object.freeze(out);
}

/**
 * Rebuild a spine from ``skeleton`` (the config-derived, all-``queued`` row
 * list) while carrying every COMPLETED row
 * (``drafted | needs_approval | flagged``) forward from ``prior`` with its
 * metadata. Non-completed rows (``queued | drafting``) reset to a fresh
 * ``queued`` state so a follow-up run can drive them via SSE.
 *
 * HARD INVARIANT for bulk reseeds: a completed row can never be downgraded to
 * ``queued`` by a spine-wide rebuild. The only legal way a completed section
 * returns to ``queued`` is an explicit single-section regen
 * (``setSectionStatus`` / the backend regenerate route) — never a config
 * reseed or a new-run reseed. This is what keeps "Draft more sections" from
 * wiping the badges of already-drafted sections.
 *
 * Pure; returns a new list, inputs untouched.
 */
export function reseedPreservingCompleted(
  skeleton: readonly SpineSection[],
  prior: readonly SpineSection[],
): SpineSection[] {
  // Custom-submission guard: a custom (build-on-existing-document) spine is
  // the writer's own headings (``CUSTOM-N`` ids), NOT the doctype template the
  // ``skeleton`` is built from. When ``prior`` shares NO ids with ``skeleton``
  // the two describe different spines, so a reseed would silently DROP every
  // loaded row (the map below only emits skeleton ids) — flattening a hydrated
  // custom spine back to the 96-module template skeleton. Preserve ``prior``
  // untouched in that case. (Empty ``prior`` — a genuine cold start / full
  // re-draft that flushed to [] first — still reseeds from the skeleton.)
  if (prior.length > 0) {
    const skeletonIds = new Set(skeleton.map((s) => s.id));
    if (!prior.some((s) => skeletonIds.has(s.id))) {
      return prior.map((s) => ({ ...s }));
    }
  }
  const priorById = new Map(prior.map((s) => [s.id, s]));
  return skeleton.map((s) => {
    const p = priorById.get(s.id);
    if (p !== undefined && SPINE_COMPLETED_STATUSES.has(p.status)) {
      // Carry the completed row forward so the spine shows prior work
      // while the new run drafts the newly-selected sections.
      return {
        ...s,
        status: p.status,
        subline: undefined,
        sublineStartedAt: undefined,
        wordCount: p.wordCount,
        acceptedAt: p.acceptedAt,
        citationCount: p.citationCount,
        contentGapCount: p.contentGapCount,
        regulatorFlagCount: p.regulatorFlagCount,
      };
    }
    return {
      ...s,
      status: "queued" as const,
      subline: undefined,
      sublineStartedAt: undefined,
      wordCount: undefined,
      acceptedAt: undefined,
    };
  });
}

/**
 * Apply a batch of section updates to a list of sections. Pure;
 * returns a new list (input untouched) and a ``changed`` flag the
 * caller uses to decide whether to push to React state.
 *
 * Each update merges only the fields it carries (status / subline /
 * sublineStartedAt / citationCount / wordCount / acceptedAt /
 * contentGapCount) — absent
 * fields preserve the prior value so partial updates don't clobber
 * existing data.
 *
 * Reference equality: when an update matches a section, the merged
 * object is a NEW reference; sections without a matching update keep
 * their prior reference. ``changed`` reflects whether at least one
 * section was merged. Pure data; no React, no I/O.
 */
export function mergeSectionUpdates(
  prev: readonly SpineSection[],
  updates: readonly SpineSectionUpdate[],
): { next: SpineSection[]; changed: boolean } {
  const updatesBySection = new Map<string, SpineSectionUpdate>();
  for (const update of updates) {
    // Last update wins for a section id (e.g. snapshot says queued but
    // current_module promotes the same section to drafting).
    updatesBySection.set(update.sectionId, update);
  }

  let changed = false;
  const next = prev.map((s) => {
    const u = updatesBySection.get(s.id);
    if (!u) return s;

    const nextStatus = u.status || s.status;
    // Explicit null CLEARS the subline (undefined = no change) — the
    // convention retrievalProgress already documents. Without this a
    // proposal's "redraft ready" spinner has no way to ever stop.
    const nextSubline = u.subline === null ? undefined : (u.subline ?? s.subline);
    const nextSublineStartedAt = u.sublineStartedAt === null ? undefined : (u.sublineStartedAt ?? s.sublineStartedAt);
    const nextCitationCount = u.citationCount ?? s.citationCount;
    const nextWordCount = u.wordCount ?? s.wordCount;
    const nextAcceptedAt = u.acceptedAt ?? s.acceptedAt;
    const nextContentGapCount = u.contentGapCount ?? s.contentGapCount;
    const nextRegulatorFlagCount = u.regulatorFlagCount ?? s.regulatorFlagCount;
    // Custom-submission template match (custom_match_resolved). ``matchResolved``
    // is a one-way latch to true; matched id/title update in lockstep with it.
    const nextMatchResolved = u.matchResolved ?? s.matchResolved;
    const nextMatchedModuleId = u.matchResolved !== undefined ? (u.matchedModuleId ?? null) : s.matchedModuleId;
    const nextMatchedTitle = u.matchResolved !== undefined ? (u.matchedTitle ?? null) : s.matchedTitle;
    // Custom-submission per-section instruction. Explicit null CLEARS it
    // (undefined = no change) — same convention as subline/retrievalProgress.
    const nextInstruction = u.instruction === null ? undefined : (u.instruction ?? s.instruction);

    if (
      nextStatus === s.status &&
      nextSubline === s.subline &&
      nextSublineStartedAt === s.sublineStartedAt &&
      nextCitationCount === s.citationCount &&
      nextWordCount === s.wordCount &&
      nextAcceptedAt === s.acceptedAt &&
      nextContentGapCount === s.contentGapCount &&
      nextRegulatorFlagCount === s.regulatorFlagCount &&
      nextMatchResolved === s.matchResolved &&
      nextMatchedModuleId === s.matchedModuleId &&
      nextMatchedTitle === s.matchedTitle &&
      nextInstruction === s.instruction
    ) {
      return s;
    }

    changed = true;
    return {
      ...s,
      status: nextStatus,
      subline: nextSubline,
      sublineStartedAt: nextSublineStartedAt,
      citationCount: nextCitationCount,
      wordCount: nextWordCount,
      acceptedAt: nextAcceptedAt,
      contentGapCount: nextContentGapCount,
      regulatorFlagCount: nextRegulatorFlagCount,
      matchResolved: nextMatchResolved,
      matchedModuleId: nextMatchedModuleId,
      matchedTitle: nextMatchedTitle,
      instruction: nextInstruction,
    };
  });
  return { next, changed };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const LIVE_RUN_STATES = new Set(["mid-run", "pausing", "approval"]);

function isSpineStatus(value: string): value is SpineSectionStatus {
  return (
    value === "queued" ||
    value === "drafting" ||
    value === "drafted" ||
    value === "needs_approval" ||
    value === "flagged" ||
    value === "skipped"
  );
}

function hasSpineStatus(item: SpineItemDTO): item is SpineItemDTO & { status: SpineSectionStatus } {
  return isSpineStatus(item.status);
}

function statusRank(status: SpineSectionStatus): number {
  switch (status) {
    case "queued":
      return 0;
    // Activation-skipped ranks with queued: any later real drafting
    // signal (explicit re-run overrides the skip) must win.
    case "skipped":
      return 0;
    case "drafting":
      return 1;
    case "drafted":
      return 2;
    case "needs_approval":
      return 3;
    case "flagged":
      return 4;
  }
}

export function useVariantCRun(params: UseVariantCRunParams): UseVariantCRunState {
  const {
    runId,
    initialSections,
    dispatch,
    onProgressEvent,
    subscriptionNonce = 0,
    subscriptionStartMode = "history",
    terminalRunStatus = false,
    auditActive = false,
    restoreInFlight = false,
    initialIdentity,
  } = params;

  const [sections, setSections] = useState<SpineSection[]>(initialSections);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [identity, setIdentity] = useState<RunIdentity | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [initStatus, setInitStatus] = useState<InitProgress | null>(null);
  const lastStreamIdRef = useRef<string | null>(null);
  // Wall-clock timestamp of the last received SSE event. Updated in
  // the onEvent wrapper inside the subscribe effect (below). The
  // watchdog interval fires every 15 s and triggers a stale-stream
  // warning if no event arrived in the previous 20 s — long enough
  // to ignore normal inter-event gaps during a run.
  const lastEventAtRef = useRef<number>(Date.now());
  // Bumped by the online listener to trigger an unconditional
  // reconnect after the browser regains network connectivity.
  const [networkReconnectNonce, setNetworkReconnectNonce] = useState(0);
  // Bumped by the idle watchdog when a live stream goes silent long
  // enough to indicate a likely stale EventSource after refresh.
  const [idleReconnectNonce, setIdleReconnectNonce] = useState(0);
  // Bumped to re-attempt the audit-only stream when it closes before its first
  // event — covers the brief prime/subscribe race on an idle run (the server's
  // ensure_stream may not have landed when the client first connected). Bounded
  // by ``auditRetryRef`` so a genuinely dead stream doesn't retry forever.
  const [auditRetryNonce, setAuditRetryNonce] = useState(0);
  const auditRetryRef = useRef(0);
  const idleReconnectIssuedRef = useRef(false);
  const subscriptionAttemptRef = useRef(0);
  const firstEventSeenRef = useRef(false);
  const subscriptionStartedAtRef = useRef(0);

  // Refs to keep the SSE handler stable across re-renders without
  // resubscribing. The handler reads sections via a ref-by-id index.
  //
  // # DECISION (Phase 9): the ref is updated SYNCHRONOUSLY inside
  // #   ``applySectionUpdates`` BEFORE ``setSections`` schedules
  // #   the React re-render. The state→ref sync effect remains as
  // #   a safety net for any caller that mutates ``sections`` via
  // #   a path that doesn't go through ``applySectionUpdates``.
  // # WHY: SSE delivers ``draft_ready`` and ``pipeline_complete``
  // #      back-to-back in the same JS tick. Without a synchronous
  // #      ref write, the run_complete handler reads a stale
  // #      ``sectionsRef.current`` and tallies ``0/N drafted`` even
  // #      when sections completed. Confirmed in production
  // #      (chat showed "Run complete — 0/38 drafted" after §7
  // #      finished). Idempotent ref writes are safe in Strict
  // #      Mode — the updater is invoked twice in dev, both times
  // #      producing the same ``next``, so the ref ends up correct.
  // # DATE: 2026-04-27
  const sectionsRef = useRef<SpineSection[]>(initialSections);
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  const activeSectionIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeSectionIdRef.current = activeSectionId;
  }, [activeSectionId]);

  const dispatchRef = useRef(dispatch);
  useEffect(() => {
    dispatchRef.current = dispatch;
  }, [dispatch]);

  // Mirrors initialIdentity so the sections-reset effect can read the
  // latest value without declaring it as a dependency (which would cause
  // the reset to fire on identity changes, not just section list changes).
  const initialIdentityRef = useRef<RunIdentity | null>(initialIdentity ?? null);
  useEffect(() => {
    initialIdentityRef.current = initialIdentity ?? null;
  }, [initialIdentity]);

  const onProgressEventRef = useRef(onProgressEvent);
  useEffect(() => {
    onProgressEventRef.current = onProgressEvent;
  }, [onProgressEvent]);

  // Reset state when initialSections changes (new run / new doc).
  // Compare by length + first id — cheap signature, avoids deep equal.
  const initialSignature = useMemo(
    () => `${initialSections.length}:${initialSections[0]?.id ?? ""}`,
    [initialSections],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: initialSignature is a stable proxy for initialSections identity; effect body reads initialSections directly but re-runs only on signature change
  useEffect(() => {
    // Preserve already-completed rows across a config reseed instead of
    // flattening the whole spine back to ``queued``. This effect fires
    // whenever ``initialSignature`` changes — including docConfig being
    // re-provided mid-session — and a blind ``setSections(initialSections)``
    // here wiped the drafted/needs_approval/flagged badges of prior
    // sections the instant "Draft more sections" ran (same run_id, so the
    // runId-reset effect below never gets a chance to preserve them).
    // See reseedPreservingCompleted's HARD INVARIANT.
    setSections((prev) => reseedPreservingCompleted(initialSections, prev));
    setActiveSectionId(null);
    // Preserve a restored identity (set from useSessionBootstrap before
    // docConfig arrived). Without this, the async docConfig fetch completes
    // in a later render and this effect wipes identity back to null, while
    // the seeding effect below doesn't re-fire because initialIdentity
    // didn't change in that render. Only resets to null on a genuine cold
    // start where no initialIdentity was ever seeded.
    setIdentity(initialIdentityRef.current ?? null);
    setStreamError(null);
  }, [initialSignature]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: runId is the reactive trigger for clearing the ref; body intentionally assigns without reading runId
  useEffect(() => {
    lastStreamIdRef.current = null;
  }, [runId]);

  // Reset sections when runId changes to a new value (new run started).
  // This is distinct from the initialSignature effect above — that one
  // fires when initialSections changes (docConfig changes). This one fires
  // when a new run starts, even if docConfig (and thus initialSections)
  // hasn't changed. Critical for "Draft more sections" flow where the same
  // docConfig is used but different modules are selected (runId changes,
  // initialSections signature stays the same).
  //
  // PRESERVE already-drafted work: "Draft more sections" builds on the
  // existing draft, so rows already in a COMPLETED status
  // (drafted | needs_approval | flagged — same set computeLockedModuleIds
  // locks in the picker) keep their status + metadata across the new run.
  // Only non-completed rows (queued | drafting) reset to a fresh queued
  // state — those are the modules the new run will (re)draft, and the
  // backend's run-scoped spine only repaints THOSE, so preserving the
  // completed rows here is what keeps them visible. A genuine full re-draft
  // goes through handleReset → hydrateSections([]) first, so this can't
  // resurrect stale rows. On RUN_RESTORED the prior state is still the fresh
  // queued list (draft content hydrates afterward), so preservation is a
  // no-op there.
  const previousRunIdRef = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runId is the reactive trigger; initialSections and runState are read for logging/reset but guard ensures effect body only runs on runId change
  useEffect(() => {
    if (!runId || runId === previousRunIdRef.current) return;
    previousRunIdRef.current = runId;
    setSections((prev) => reseedPreservingCompleted(initialSections, prev));
    setActiveSectionId(null);
    setStreamError(null);
  }, [runId]);

  // Seed identity from the restored prop when it arrives async (shell
  // sets it after the document_session fetch completes, typically AFTER
  // the hook has already mounted). Functional update preserves a fresh
  // SSE identity if it raced ahead.
  useEffect(() => {
    if (!initialIdentity) return;
    setIdentity((cur) => cur ?? initialIdentity);
  }, [initialIdentity]);

  // --- Section title lookup the adapter calls ----------------------------
  const sectionTitleLookup = useCallback((moduleId: string): { number: string; title: string } | null => {
    const s = sectionsRef.current.find((x) => x.id === moduleId);
    return s ? { number: s.number, title: s.title } : null;
  }, []);

  // --- Apply section updates from the adapter -----------------------------
  const applySectionUpdates = useCallback((updates: SpineSectionUpdate[]) => {
    if (updates.length === 0) return;
    // Phase 9 — compute ``next`` synchronously from the ref so we can
    // update ``sectionsRef.current`` BEFORE handing React the new
    // state. Subsequent same-tick reads (notably the RUN_COMPLETED
    // handler that tallies drafted sections) see the post-update
    // view rather than the pre-render snapshot. See the ref's
    // DECISION block above for the race the old effect-based sync
    // failed to handle.
    const result = mergeSectionUpdates(sectionsRef.current, updates);
    if (!result.changed) return;
    sectionsRef.current = result.next;
    setSections(result.next);

    // Clear activeSectionId if the active section transitions from
    // drafting to drafted (e.g., when a regen completes). This stops
    // the pulse animation and active highlighting when the section
    // is no longer being actively drafted.
    for (const update of updates) {
      if (update.status === "drafted" && activeSectionIdRef.current === update.sectionId) {
        const section = result.next.find((s) => s.id === update.sectionId);
        if (section && section.status === "drafted") {
          setActiveSectionId(null);
          break;
        }
      }
    }
  }, []);

  // --- SSE event handler ---------------------------------------------------
  const handleEvent = useCallback(
    (event: ProgressEvent, streamId?: string) => {
      if (streamId) lastStreamIdRef.current = streamId;
      // Fire the side-effect callback first so the shell's Word-write
      // logic sees events in the same order they arrive. We don't await
      // — Word.run is async + non-blocking.
      onProgressEventRef.current?.(event);
      // Terminal audit events also feed the module-scope review store so
      // a panel attached to a run it didn't start (reloaded add-in,
      // reopened inspector) flips running → done/error live. Sublines
      // keep flowing through translateProgressEvent below.
      if (runId && event.event_type?.startsWith("audit_")) {
        handleAuditSseEvent(runId, event);
      }

      const {
        lifecycleEvents,
        sectionUpdates,
        eventLogAppends,
        identity: nextIdentity,
        initStatus: nextInitStatus,
      } = translateProgressEvent(event, sectionTitleLookup);

      if (nextIdentity) {
        setIdentity(nextIdentity);
      }
      // nextInitStatus is undefined when the event doesn't touch init state;
      // null means "clear the banner"; a string means "show this message".
      if (nextInitStatus !== undefined) {
        setInitStatus(nextInitStatus);
      }

      // Phase 9 — apply section updates BEFORE lifecycle events so
      // ``RUN_COMPLETED`` reads the just-updated ``sectionsRef`` and
      // tallies drafted sections correctly. The synchronous
      // ref-write inside ``applySectionUpdates`` makes this ordering
      // load-bearing: events delivered in the same tick (e.g.
      // ``draft_ready`` immediately followed by ``pipeline_complete``)
      // now compose the way the writer expects.
      applySectionUpdates(sectionUpdates);

      // Track active section locally so the spine can highlight it
      // even if the reducer's own activeSection lags by a render.
      for (const ev of lifecycleEvents) {
        if (ev.type === "SECTION_ACTIVATED") {
          setActiveSectionId(ev.section.sectionId);
        }
        // Patch RUN_COMPLETED summary with sectionsRef-derived totals
        // (the adapter has no view of the section list). Also clear
        // the active-section pointer — at post-run, nothing is being
        // drafted; the spine's mote layer + active-row pulse should
        // both stop. Sections that still need writer attention stay
        // visually distinct via their own status (needs_approval keeps
        // pulsing yellow regardless of activeSectionId, by design —
        // that's the writer-attention signal).
        if (ev.type === "RUN_COMPLETED") {
          const all = sectionsRef.current;
          const drafted = all.filter((s) => s.status === "drafted").length;
          const flagged = all.filter((s) => s.status === "flagged").length;
          setActiveSectionId(null);
          // Phase 8.5 — record the run-complete lifecycle bracket BEFORE
          // RUN_COMPLETED so the EventLog still has the run scope when
          // the entry lands. The chat surface projects this to a
          // "Run complete — N/M drafted, K flagged in Xm Ys" system note.
          dispatchRef.current({
            type: "EVENT_APPENDED",
            entry: {
              ts: Date.now(),
              actor: "asthra",
              sectionId: null,
              runId: null,
              payload: {
                kind: "run_complete",
                totalSections: all.length,
                draftedSections: drafted,
                flaggedSections: flagged,
                durationMs: ev.summary.durationMs,
              },
            },
          });
          dispatchRef.current({
            type: "RUN_COMPLETED",
            summary: {
              ...ev.summary,
              totalSections: all.length,
              draftedSections: drafted,
              flaggedSections: flagged,
            },
          });
          continue;
        }
        if (ev.type === "RUN_PAUSED") {
          console.info(
            `[sse-debug] run_paused_event runId=${runId ?? "null"} streamId=${streamId ?? "none"} lastStreamId=${lastStreamIdRef.current ?? "none"}`,
          );
          // Keep the active section pinned as the paused resume point.
          // The row remains orange, while animation is suppressed by
          // LoomSpine's pauseAnimations prop from shell state.
          dispatchRef.current(ev);
          continue;
        }
        // Same treatment for hard failures — stop the visual run
        // signals so the writer doesn't see a corpse-pulse on a dead run.
        if (ev.type === "RUN_FAILED") {
          setActiveSectionId(null);
          dispatchRef.current(ev);
          continue;
        }
        dispatchRef.current(ev);
      }

      // Fan out EventLog appends. Fires AFTER lifecycle dispatches so
      // events the log records have already caused any state transitions
      // they were going to — the log is observational; lifecycle is
      // authoritative. Order within eventLogAppends preserves backend
      // arrival order (adapter builds it chronologically).
      for (const entry of eventLogAppends) {
        dispatchRef.current({ type: "EVENT_APPENDED", entry });
      }
    },
    [applySectionUpdates, sectionTitleLookup, runId],
  );

  // --- Subscribe lifecycle -------------------------------------------------
  useEffect(() => {
    if (!runId) return;
    if (restoreInFlight) {
      console.info(`[sse-debug] subscribe_skipped reason=restoreInFlight runId=${runId}`);
      setStreamError(null);
      return;
    }
    if (params.runState === "paused") {
      console.info(`[sse-debug] subscribe_skipped reason=run_paused runId=${runId}`);
      setStreamError(null);
      return;
    }
    // Restored terminal runs have no live Redis stream — subscribing
    // would 404 and surface a spurious "Connection lost" banner. The
    // timeline + spine are already hydrated from Mongo via REST.
    //
    // EXCEPTION: a manual section audit ("Run review") can run on a terminal
    // run. It DOES produce a live stream (the audit worker emits ``audit_*``
    // events; the server primes the stream via ``ensure_stream`` before the
    // job is enqueued). Keep a stream open for that window so the audit's step
    // + counter narration reaches the panel; ``auditOnlyStream`` below keeps it
    // ephemeral (latest-only, banner suppressed, brief connect retry).
    const auditOnlyStream = terminalRunStatus && auditActive;
    if (terminalRunStatus && !auditActive) {
      console.info(`[sse-debug] subscribe_skipped reason=terminal_run runId=${runId}`);
      setStreamError(null);
      return;
    }
    setStreamError(null);
    // Optimistically show the "Preparing run" card the instant a fresh run
    // subscribes, instead of clearing to null and staring at an empty spine
    // for the 10–30s the child subprocess takes to boot (fork + heavy imports
    // + init_beanie + user-model resolve) before the first SSE event. Only for
    // a genuinely fresh run's first connect: ``history`` mode is a brand-new
    // run (resume/refresh uses ``latest``), and a null lastStreamId means no
    // event has been seen yet (a reconnect has one). On resume/reconnect we
    // still clear to null so we never paint a stale card over a live spine.
    if (!auditOnlyStream && subscriptionStartMode === "history" && lastStreamIdRef.current == null) {
      setInitStatus({ stage: "booting", message: "Starting the drafting engine…" });
    } else {
      setInitStatus(null);
    }
    lastEventAtRef.current = Date.now(); // reset watchdog clock on each new subscription
    idleReconnectIssuedRef.current = false;
    firstEventSeenRef.current = false;
    subscriptionStartedAtRef.current = Date.now();
    subscriptionAttemptRef.current += 1;
    const attemptId = subscriptionAttemptRef.current;
    // Audit-only stream on a terminal run: start from the tail so we don't
    // replay the whole completed run's history, only the fresh audit events.
    const lastEventId =
      lastStreamIdRef.current ?? (subscriptionStartMode === "latest" || auditOnlyStream ? "latest" : null);
    console.info(
      `[sse-debug] subscribe_start attempt=${attemptId} runId=${runId} mode=${subscriptionStartMode} runState=${params.runState ?? "unknown"} lastStreamId=${lastStreamIdRef.current ?? "none"} lastEventId=${lastEventId ?? "none"} subscriptionNonce=${subscriptionNonce} networkReconnectNonce=${networkReconnectNonce} idleReconnectNonce=${idleReconnectNonce}`,
    );
    const cleanup = subscribeToRun(
      runId,
      {
        onEvent: (data, eventId) => {
          lastEventAtRef.current = Date.now();
          idleReconnectIssuedRef.current = false;
          if (!firstEventSeenRef.current) {
            firstEventSeenRef.current = true;
            auditRetryRef.current = 0; // stream is live — reset the audit connect-retry budget
            const firstEventDelayMs = Date.now() - subscriptionStartedAtRef.current;
            console.info(
              `[sse-debug] first_event attempt=${attemptId} runId=${runId} eventType=${data.event_type} eventId=${eventId ?? "none"} delayMs=${firstEventDelayMs}`,
            );
          }
          handleEvent(data, eventId);
        },
        onError: () => {
          console.warn(
            `[sse-debug] stream_error attempt=${attemptId} runId=${runId} runState=${params.runState ?? "unknown"} lastStreamId=${lastStreamIdRef.current ?? "none"}`,
          );
          // Only show the banner during live states. A CLOSED EventSource
          // for a paused / post-run / idle run is expected (Redis stream
          // may already be gone) and the banner would be misleading.
          if (LIVE_RUN_STATES.has(params.runState ?? "")) {
            setStreamError("Connection to server lost — auto-reconnecting. Drafted sections are safe.");
          } else if (auditOnlyStream && !firstEventSeenRef.current && auditRetryRef.current < 12) {
            // Audit-only stream closed before its first event — most likely the
            // prime/subscribe race on an idle run. Retry a few times (~0.8s
            // apart, ≤ ~10s) so the audit's live steps still arrive. No banner:
            // the awaited POST still returns the findings regardless.
            auditRetryRef.current += 1;
            setTimeout(() => setAuditRetryNonce((n) => n + 1), 800);
          }
        },
        onComplete: () => {
          console.info(
            `[sse-debug] stream_complete attempt=${attemptId} runId=${runId} lastStreamId=${lastStreamIdRef.current ?? "none"}`,
          );
          // No-op — pipeline_complete already dispatches RUN_COMPLETED
          // through the adapter; the EventSource closes itself.
        },
      },
      { lastEventId },
    );

    // Idle-stream watchdog: if no event arrives within 20 s, surface
    // a warning so the writer knows the connection may be stale.
    // Clears on cleanup with the subscription itself.
    const WATCHDOG_POLL_MS = 15_000;
    const WATCHDOG_IDLE_MS = 20_000;
    const watchdog = setInterval(() => {
      const idleForMs = Date.now() - lastEventAtRef.current;
      if (idleForMs > WATCHDOG_IDLE_MS) {
        // Only surface the idle warning when the run should be actively
        // streaming. For paused / post-run / idle states the silence is
        // expected, so suppress the confusing banner.
        if (LIVE_RUN_STATES.has(params.runState ?? "")) {
          setStreamError("Connection idle — reconnecting stream…");
          if (!idleReconnectIssuedRef.current) {
            idleReconnectIssuedRef.current = true;
            console.warn(
              `[sse-debug] idle_reconnect_trigger attempt=${attemptId} runId=${runId} idleForMs=${idleForMs} lastStreamId=${lastStreamIdRef.current ?? "none"} runState=${params.runState ?? "unknown"}`,
            );
            setIdleReconnectNonce((n) => n + 1);
          }
        } else {
          setStreamError(null);
        }
      }
    }, WATCHDOG_POLL_MS);

    return () => {
      console.info(
        `[sse-debug] subscribe_cleanup attempt=${attemptId} runId=${runId} lastStreamId=${lastStreamIdRef.current ?? "none"}`,
      );
      cleanup();
      clearInterval(watchdog);
    };
  }, [
    runId,
    handleEvent,
    subscriptionNonce,
    subscriptionStartMode,
    networkReconnectNonce,
    idleReconnectNonce,
    auditRetryNonce,
    params.runState,
    restoreInFlight,
    terminalRunStatus,
    auditActive,
  ]);

  // Clear any lingering stream banner when the run transitions out of a
  // live state (e.g. mid-run → post-run after RUN_COMPLETED dispatch).
  // Without this the banner set during the brief mid-run window survives
  // into post-run because the subscribe effect's setStreamError(null)
  // path is short-circuited by the terminalRunStatus gate above.
  useEffect(() => {
    if (!LIVE_RUN_STATES.has(params.runState ?? "")) {
      setStreamError(null);
    }
  }, [params.runState]);

  // Snapshot safety-net: while a run is live, periodically reconcile the
  // spine from the backend snapshot. This heals missed SSE windows on
  // refresh/re-attach paths that subscribe with ``lastEventId=latest``.
  useEffect(() => {
    if (!runId) return;
    if (restoreInFlight) return;
    if (terminalRunStatus) return;
    if (!LIVE_RUN_STATES.has(params.runState ?? "")) return;

    let cancelled = false;
    let inFlight = false;

    const reconcileFromSnapshot = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const snapshot = await getRunSnapshot(runId);
        if (cancelled) return;

        const currentById = new Map(sectionsRef.current.map((section) => [section.id, section]));
        const updates: SpineSectionUpdate[] = snapshot.spine
          // Normalize post-``drafted`` lifecycle states (verified/inserted/
          // edited) onto the spine status BEFORE the type-guard filter —
          // mirrors the live SSE path. Without this the raw advanced statuses
          // fail ``hasSpineStatus``, get dropped, and a Saved section reloads
          // as a hollow/queued dot, vanishing from the drafted count.
          .map((item) => ({ ...item, status: normalizeSpineLifecycle(item.status) }))
          .filter(hasSpineStatus)
          .map((item) => {
            const current = currentById.get(item.id);
            const allowStatusUpdate =
              current === undefined ? true : statusRank(item.status) >= statusRank(current.status);
            const acceptedAt = item.accepted_at ? Date.parse(item.accepted_at) : undefined;
            return {
              sectionId: item.id,
              status: allowStatusUpdate ? item.status : undefined,
              citationCount: allowStatusUpdate ? (item.citation_count ?? undefined) : undefined,
              wordCount: allowStatusUpdate ? (item.word_count ?? undefined) : undefined,
              acceptedAt: allowStatusUpdate ? acceptedAt : undefined,
              contentGapCount: allowStatusUpdate ? (item.content_gap_count ?? undefined) : undefined,
            };
          });

        // The backend's ``current_module`` can lag reality: the wrapper
        // re-points it at the JUST-COMPLETED module after every section,
        // and during the end-of-run regulatory-audit drain (audits join
        // BEFORE pipeline_complete) the Redis live pointer is cleared, so
        // the snapshot falls back to that stale Mongo value for minutes.
        // An unguarded push here repainted a drafted section as
        // "drafting" every poll (done → undone). Never downgrade a
        // completed row — same invariant as the statusRank guard above.
        const currentModuleSection = snapshot.current_module ? currentById.get(snapshot.current_module) : undefined;
        const currentModuleIsCompleted =
          currentModuleSection !== undefined && SPINE_COMPLETED_STATUSES.has(currentModuleSection.status);
        if (snapshot.current_module && !currentModuleIsCompleted) {
          updates.push({
            sectionId: snapshot.current_module,
            status: "drafting",
          });
        }

        if (updates.length > 0) {
          applySectionUpdates(updates);
        }

        if (snapshot.current_module && !currentModuleIsCompleted) {
          setActiveSectionId(snapshot.current_module);
        }
        if (snapshot.status === "completed" || snapshot.status === "failed" || snapshot.status === "cancelled") {
          setActiveSectionId(null);
        }
      } catch (err) {
        console.warn("[sse] snapshot reconcile failed:", err);
      } finally {
        inFlight = false;
      }
    };

    void reconcileFromSnapshot();
    const poll = setInterval(() => {
      void reconcileFromSnapshot();
    }, 12_000);

    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [runId, params.runState, restoreInFlight, terminalRunStatus, applySectionUpdates]);

  // --- Offline / online reconnection --------------------------------------
  // When the browser regains connectivity, clear the stale-stream
  // warning and bump ``networkReconnectNonce`` to trigger a fresh
  // subscription (same mechanism as ``runSubscriptionNonce`` in
  // VariantCShell). The offline handler just surfaces the banner;
  // EventSource will auto-reconnect on its own once the browser
  // reports network again.
  useEffect(() => {
    const handleOffline = () => {
      setStreamError("No network connection — events paused");
    };
    const handleOnline = () => {
      setStreamError(null);
      setNetworkReconnectNonce((n) => n + 1);
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  // Imperative setter exposed for higher-level effects (verdict
  // watcher → popout flow). Goes through applySectionUpdates so the
  // change merges idempotently with whatever the SSE stream is doing.
  const setSectionStatus = useCallback(
    (sectionId: string, status: SpineSectionStatus) => {
      applySectionUpdates([{ sectionId, status }]);
    },
    [applySectionUpdates],
  );

  // Imperative setter for a custom section's drafting instruction, exposed so
  // the Inspector's Instructions editor can push a save through the same merge
  // path the SSE stream uses. ``null`` clears it (drops the section back below
  // the draftable threshold); a non-empty string sets it.
  const setSectionInstruction = useCallback(
    (sectionId: string, instruction: string | null) => {
      applySectionUpdates([{ sectionId, instruction }]);
    },
    [applySectionUpdates],
  );

  // Seed the resume position for the NEXT (re)subscribe. Used by the bulk
  // resolve flow: the endpoint returns the stream tail id captured just before
  // dispatch, and seeding it here makes the re-subscribe replay the resolve's
  // events (module_start onward) instead of tailing from "latest" and racing-
  // missing that first event. lastStreamIdRef wins over the mode fallback in
  // the subscribe effect (see lastEventId construction).
  const seedStreamCursor = useCallback((id: string | null) => {
    lastStreamIdRef.current = id;
  }, []);

  // Imperative "activity is over" clear for a section: wipes the
  // subline/spinner (and optionally sets a status) regardless of SSE
  // delivery. Belt-and-braces companion to the section_state_changed
  // handler — a proposal decision must never leave a spinner behind
  // just because an event was missed.
  const clearSectionActivity = useCallback(
    (sectionId: string, status?: SpineSectionStatus) => {
      applySectionUpdates([
        {
          sectionId,
          ...(status ? { status } : {}),
          subline: null,
          sublineStartedAt: null,
        },
      ]);
    },
    [applySectionUpdates],
  );

  // Every bulk spine write funnels through here, so this is the one
  // place that guarantees document order regardless of how the caller
  // assembled the list (REST spine order, persisted snapshot, reconcile).
  const hydrateSections = useCallback((incoming: SpineSection[]) => {
    const nextSections = orderSpineSections(incoming);
    sectionsRef.current = nextSections;
    setSections(nextSections);
    const draftingSection = nextSections.find((section) => section.status === "drafting");
    setActiveSectionId(draftingSection?.id ?? null);
  }, []);

  return {
    sections,
    activeSectionId,
    identity,
    streamError,
    initStatus,
    setSectionStatus,
    setSectionInstruction,
    seedStreamCursor,
    clearSectionActivity,
    hydrateSections,
  };
}
